import { useState } from 'react';
import { useSearchParams } from 'react-router';

import type { ApiScan } from '@kaiizen/kai-pro-api';
import {
  ScanEditingContextProvider,
  useAuth,
  useFeatureFlags,
  useKaiProApiClient,
  useScanEditingContextSlice,
  useScansByPropertyProjectId,
} from '@kaiizen/kai-pro-api-react-state';

import { ANALYTICS_EVENTS, trackEvent } from '@/Client/analytics';
import { triggerDownload } from '@/Client/Lib';
import { useNavigateToScan, useToast } from '@/Client/State';
import { FocusPanelTitlebar } from '../FocusPanelTitlebar';
import type { ScanFocusPanelScopeProps } from '../scanFocusPanelTypes';
import { AudienceDocArtifactDocument } from './AudienceDocArtifactDocument';
import { AudienceDocCopiesSection } from './AudienceDocCopiesSection';
import { CreateAudienceDocModal } from './CreateAudienceDocModal';
import {
  getArtifactTime,
  getDefaultAudienceDocName,
  getHistoricalArtifactDocumentType,
  getHistoricalArtifactTitle,
  getScopeArtifactDocuments,
  type HistoricalArtifactDocumentType,
  isAudienceDocScan,
  isCloseoutArtifactScan,
  isHistoricalArtifactScan,
  isHistoricalScopeArtifactScan,
  isScopeArtifactScan,
} from './FocusedArtifactPanelLib';
import { HistoricalArtifactsSection } from './HistoricalArtifactsSection';
import { HistoricalScopeArtifactDocument } from './HistoricalScopeArtifactDocument';

/**
 * Renders the selected artifact against its own scan-editing context — this is
 * why the photos-zip download (needs this artifact's own `scanRoot`, not the
 * main scan's) lives here rather than in the outer panel.
 */
const FocusedArtifactDocument = ({
  artifactScan,
  baseArtifact,
  basedOnTitle,
  canManageShare,
  artifactTitle,
  artifactDocumentType,
  returnToArtifactList,
}: {
  artifactScan: ApiScan;
  baseArtifact?: ApiScan;
  basedOnTitle: string;
  canManageShare: boolean;
  artifactTitle: string;
  artifactDocumentType?: HistoricalArtifactDocumentType;
  returnToArtifactList: () => void;
}) => {
  const apiClient = useKaiProApiClient();
  const { showErrorToast } = useToast();
  const { scanRoot } = useScanEditingContextSlice(['scanRoot']);

  /**
   * Same download the old "View Scope" page's top-left button offers
   * (ScopeToolbar.tsx) — a short-lived read-only token, then a direct
   * navigation to the streaming zip endpoint.
   */
  const downloadScopePhotosZip = async (scanId: string) => {
    try {
      const { token } = await apiClient.generateReadOnlyToken();
      triggerDownload(apiClient.getScopeReportPhotosZipUrl(scanId, token));
      trackEvent(ANALYTICS_EVENTS.scanExportedPhotosZip, { scanId, reportType: 'historical_artifact' });
    } catch (error) {
      showErrorToast(error, "download this scope's photos");
    }
  };

  return isAudienceDocScan(artifactScan)
    ? (
      <AudienceDocArtifactDocument
        audienceDoc={artifactScan}
        baseArtifact={baseArtifact}
        basedOnTitle={basedOnTitle}
        canManageShare={canManageShare}
        onBack={returnToArtifactList}
      />
    )
    : (
      <HistoricalScopeArtifactDocument
        artifactTitle={artifactTitle}
        documentType={artifactDocumentType}
        scan={artifactScan}
        photoCount={scanRoot?.optimized.allPhotos.length}
        showPurchaseOrderExports={artifactScan.scanLifecycleStatus === 'approved'}
        onDownloadPhotosZip={scanRoot?.optimized.allPhotos.length
          ? () => downloadScopePhotosZip(artifactScan.id)
          : undefined}
        onBack={returnToArtifactList}
      />
    );
};

/** Artifact workspace for project scope history and audience-doc copies. */
export const FocusedArtifactPanel = ({ artifactScanId, ...props }: ScanFocusPanelScopeProps & {
  artifactScanId?: string;
}) => {
  const { featureFlags } = useFeatureFlags();
  const { userCan } = useAuth();
  const apiClient = useKaiProApiClient();
  const navigateToScan = useNavigateToScan();
  const { showToast, showErrorToast } = useToast();
  const { project, scan, scanId, scanIsEditable } = useScanEditingContextSlice(['project', 'scan', 'scanId', 'scanIsEditable']);
  const [searchParams] = useSearchParams();
  const { scans: projectScans, isLoading } = useScansByPropertyProjectId(
    project?.id,
    { includeAudienceDocs: true },
  );
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreatingAudienceDoc, setIsCreatingAudienceDoc] = useState(false);
  const [createdAudienceDoc, setCreatedAudienceDoc] = useState<ApiScan>();

  const orderedScans = [...(projectScans ?? [])].sort(
    (left, right) => getArtifactTime(left.createdAt) - getArtifactTime(right.createdAt),
  );
  const scopeArtifacts = orderedScans.filter(isScopeArtifactScan);
  const historicalScopeArtifacts = scopeArtifacts.filter(isHistoricalScopeArtifactScan);
  const closeoutArtifact = orderedScans.find(
    scan => scan.id === project?.firstApprovedCloseoutScanId && isCloseoutArtifactScan(scan),
  )
    ?? orderedScans.find(isCloseoutArtifactScan);
  const historicalScopeArtifactIds = new Set(
    historicalScopeArtifacts.map(scopeArtifact => scopeArtifact.id),
  );
  const audienceDocs = [
    ...orderedScans
      .filter(isAudienceDocScan)
      .filter(audienceDoc => historicalScopeArtifactIds.has(audienceDoc.parentScanId ?? '')),
  ].sort(
    (left, right) => getArtifactTime(right.createdAt) - getArtifactTime(left.createdAt),
  );

  const changeOrderNumberById = new Map<string, number>();
  let changeOrderNumber = 0;
  for (const scopeArtifact of historicalScopeArtifacts) {
    if (scopeArtifact.scanType !== 'changeRequest') continue;
    changeOrderNumber += 1;
    changeOrderNumberById.set(scopeArtifact.id, changeOrderNumber);
  }

  const scopeArtifactTitlesById = new Map<string, string>();
  for (const scopeArtifact of historicalScopeArtifacts) {
    scopeArtifactTitlesById.set(
      scopeArtifact.id,
      getHistoricalArtifactTitle(scopeArtifact, changeOrderNumberById),
    );
  }

  const latestApprovedScopeArtifactId = [...historicalScopeArtifacts]
    .reverse()
    .find(scopeArtifact => scopeArtifact.scanLifecycleStatus === 'approved')
    ?.id;
  const selectedArtifact = searchParams.get('artifacts') === 'list'
    ? undefined
    : orderedScans.find(artifact => artifact.id === artifactScanId)
      ?? (createdAudienceDoc?.id === artifactScanId ? createdAudienceDoc : undefined);
  const selectedArtifactDocumentType = selectedArtifact && isHistoricalArtifactScan(selectedArtifact)
    ? getHistoricalArtifactDocumentType(selectedArtifact, searchParams.get('artifactDocument'))
    : undefined;
  const selectedScopeArtifactId = selectedArtifact?.scanType === 'audienceDoc'
    ? selectedArtifact.parentScanId
    : selectedArtifact && isScopeArtifactScan(selectedArtifact)
    ? selectedArtifact.id
    : undefined;
  const selectedAudienceDocId = selectedArtifact?.scanType === 'audienceDoc'
    ? selectedArtifact.id
    : undefined;
  const currentBaseArtifact = historicalScopeArtifacts.find(
    scopeArtifact => scopeArtifact.id === selectedScopeArtifactId,
  ) ?? historicalScopeArtifacts.at(-1);
  const audienceDocsEnabled = !!featureFlags.newScopeBuilderFlow;
  const canEditAudienceDocs = featureFlags.turnBasedHandoff
    ? scanIsEditable
    : userCan('update', 'PropertyProject');
  const canCreateAudienceDoc = !!scan
    && !scan.archivedAt
    && audienceDocsEnabled
    && userCan('create', 'Scan')
    && canEditAudienceDocs
    && historicalScopeArtifacts.length > 0;
  const canManageAudienceDocShare = !!scan
    && !scan.archivedAt
    && audienceDocsEnabled
    && canEditAudienceDocs;
  const showAudienceDocCopiesSection = audienceDocsEnabled && historicalScopeArtifacts.length > 0;
  const isViewingHistoricalArtifact = !!selectedArtifact && isHistoricalArtifactScan(selectedArtifact);
  const isViewingAudienceDoc = !!selectedArtifact && isAudienceDocScan(selectedArtifact);
  const returnToArtifactList = () => navigateToScan({ scanId, focusPanel: 'artifact' });
  const selectArtifact = (
    artifactScanId: string,
    artifactDocument?: HistoricalArtifactDocumentType,
  ) => navigateToScan({ scanId, focusPanel: 'artifact', artifactScanId, artifactDocument });

  const createAudienceDoc = async ({
    includePhotos,
    name,
    parentScanId,
    photosOnly,
    residentResponsibleOnly,
    showAmounts,
  }: {
    includePhotos: boolean;
    name: string;
    parentScanId: string;
    photosOnly: boolean;
    residentResponsibleOnly: boolean;
    showAmounts: boolean;
  }) => {
    if (!canCreateAudienceDoc) return;
    if (!name.trim()) {
      showToast('Name is required.', 'error');
      return;
    }

    setIsCreatingAudienceDoc(true);
    try {
      const audienceDoc = await apiClient.createScan({
        name: name.trim(),
        parentScanId,
        scanType: 'audienceDoc',
        customerMetadata: {
          audienceDocIncludePhotos: includePhotos,
          audienceDocPhotosOnly: photosOnly,
          audienceDocResidentResponsibleOnly: residentResponsibleOnly,
          audienceDocShowAmounts: showAmounts,
        },
      });
      trackEvent(ANALYTICS_EVENTS.audienceDocCreated, {
        audienceDocId: audienceDoc.id,
        parentScanId,
        source: 'artifact_panel',
      });
      trackEvent(ANALYTICS_EVENTS.scanCreated, {
        scanId: audienceDoc.id,
        parentScanId,
        scanType: 'audienceDoc',
      });
      setCreatedAudienceDoc(audienceDoc);
      selectArtifact(audienceDoc.id);
      setIsCreateModalOpen(false);
      showToast('Audience doc created.', 'success');
    } catch (error) {
      showErrorToast(error, 'create an audience doc');
    } finally {
      setIsCreatingAudienceDoc(false);
    }
  };

  return (
    <>
      <FocusPanelTitlebar
        {...props}
        bodyVariant={isViewingHistoricalArtifact || isViewingAudienceDoc ? 'flush' : 'default'}
        hideBreadcrumb={true}
      >
        {selectedArtifact && (isViewingHistoricalArtifact || isViewingAudienceDoc)
          ? (
            <ScanEditingContextProvider
              key={selectedArtifact.id}
              scanId={selectedArtifact.id}
              userHasAdminScanAccess={true}
            >
              <FocusedArtifactDocument
                artifactScan={selectedArtifact}
                baseArtifact={selectedArtifact.scanType === 'audienceDoc'
                  ? historicalScopeArtifacts.find(
                    scopeArtifact => scopeArtifact.id === selectedArtifact.parentScanId,
                  )
                  : undefined}
                artifactTitle={isCloseoutArtifactScan(selectedArtifact)
                  ? 'Closeout'
                  : isScopeArtifactScan(selectedArtifact)
                  ? getScopeArtifactDocuments(selectedArtifact, changeOrderNumberById)
                    .find(artifactDocument => artifactDocument.documentType === selectedArtifactDocumentType)
                    ?.title ?? 'Scope'
                  : scopeArtifactTitlesById.get(selectedArtifact.id) ?? 'Scope'}
                artifactDocumentType={selectedArtifactDocumentType}
                basedOnTitle={scopeArtifactTitlesById.get(selectedArtifact.parentScanId ?? '') ?? 'Scope'}
                canManageShare={canManageAudienceDocShare}
                returnToArtifactList={returnToArtifactList}
              />
            </ScanEditingContextProvider>
          )
          : (
            <>
              <HistoricalArtifactsSection
                closeoutArtifact={closeoutArtifact}
                hasAnyScopeArtifacts={scopeArtifacts.length > 0}
                latestApprovedScopeArtifactId={latestApprovedScopeArtifactId}
                isLoading={isLoading}
                scopeArtifacts={historicalScopeArtifacts}
                selectedArtifactId={selectedArtifact?.id}
                selectedArtifactDocumentType={selectedArtifactDocumentType}
                changeOrderNumberById={changeOrderNumberById}
                onSelectArtifact={selectArtifact}
              />

              {showAudienceDocCopiesSection && (
                <AudienceDocCopiesSection
                  audienceDocs={audienceDocs}
                  canCreateAudienceDoc={canCreateAudienceDoc}
                  canManageAudienceDocShare={canManageAudienceDocShare}
                  featureEnabled={audienceDocsEnabled}
                  selectedAudienceDocId={selectedAudienceDocId}
                  scopeArtifactTitlesById={scopeArtifactTitlesById}
                  onOpenCreateModal={() => setIsCreateModalOpen(true)}
                  onSelectAudienceDoc={selectArtifact}
                  onOpenAudienceDoc={selectArtifact}
                />
              )}
            </>
          )}
      </FocusPanelTitlebar>

      <CreateAudienceDocModal
        baseArtifacts={historicalScopeArtifacts}
        baseArtifactTitlesById={scopeArtifactTitlesById}
        defaultBaseArtifactId={currentBaseArtifact?.id ?? historicalScopeArtifacts.at(-1)?.id}
        defaultName={getDefaultAudienceDocName(currentBaseArtifact)}
        isCreating={isCreatingAudienceDoc}
        visible={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={createAudienceDoc}
      />
    </>
  );
};
